import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { ShieldCheck, Activity, Target, TrendingUp, Scale, FileSearch, Bot, Boxes, Database, Workflow, Plug, AlertTriangle, BrainCircuit, FlaskConical, Radar, Megaphone, ShoppingBag, Inbox, Building2, PhoneCall } from 'lucide-react'
import GovernancePosture from '../components/GovernancePosture'
import GovernanceQuality from '../components/GovernanceQuality'
import GovernanceTrust from '../components/GovernanceTrust'
import { getSession } from '../lib/session'

// Icon per product. Names now come from the registry's `title`, so this only
// needs the icon; every product the platform offers is represented.
const SERVICE_META = {
  answers: { icon: Bot },
  rag: { icon: Boxes },
  sync: { icon: Database },
  intake: { icon: Scale },
  voice: { icon: PhoneCall },
  orchestration: { icon: Workflow },
  middleware: { icon: Plug },
  agent: { icon: Bot },
  'marketing-intel': { icon: BrainCircuit },
  growth: { icon: FlaskConical },
  seo: { icon: Radar },
  campaigns: { icon: Megaphone },
  commerce: { icon: ShoppingBag },
  inbox: { icon: Inbox },
  reseller: { icon: Building2 },
}

// status → semantic classes (semantic color is separate from the brand accent)
const TONE = {
  good:     { text: 'text-green-700', bg: 'bg-green-500/10', dot: 'bg-green-500', label: 'Healthy' },
  clear:    { text: 'text-green-700', bg: 'bg-green-500/10', dot: 'bg-green-500', label: 'Clear' },
  stable:   { text: 'text-green-700', bg: 'bg-green-500/10', dot: 'bg-green-500', label: 'Stable' },
  warning:  { text: 'text-amber-700', bg: 'bg-amber-500/10', dot: 'bg-amber-500', label: 'Watch' },
  watch:    { text: 'text-amber-700', bg: 'bg-amber-500/10', dot: 'bg-amber-500', label: 'Watch' },
  critical: { text: 'text-red-700',   bg: 'bg-red-500/10',   dot: 'bg-red-500',   label: 'Action needed' },
  baseline: { text: 'text-gray-500',  bg: 'bg-gray-500/10',  dot: 'bg-gray-400',  label: 'Baseline' },
}
const tone = (s) => TONE[s] || TONE.baseline

const METRIC_ICON = { health: Activity, accuracy: Target, drift: TrendingUp, bias: Scale, explainability: FileSearch }

function StatusPill({ status }) {
  const t = tone(status)
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${t.bg} ${t.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} /> {t.label}
    </span>
  )
}

function MetricCard({ k, m }) {
  const Icon = METRIC_ICON[k] || Activity
  const t = tone(m.status)
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 text-gray-500">
          <Icon size={16} /> <span className="text-sm font-medium">{m.label}</span>
        </div>
        <StatusPill status={m.status} />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        {typeof m.score === 'number'
          ? <><span className={`text-3xl font-bold tabular-nums ${t.text}`}>{m.score}</span><span className="text-gray-400 text-lg">%</span></>
          : <span className={`text-2xl font-bold ${t.text}`}>{t.label}</span>}
      </div>
      <p className="mt-1.5 text-sm text-gray-600">{m.detail}</p>
      <p className="mt-2 text-[11px] text-gray-400 uppercase tracking-wide">{m.basis}</p>
    </div>
  )
}

export default function Governance() {
  const session = getSession()
  const [registry, setRegistry] = useState(null)
  const [metrics, setMetrics] = useState(null)
  const [err, setErr] = useState(false)

  useEffect(() => {
    if (!session?.namespaceId) return
    Promise.all([
      axios.get('/api/v1/governance/registry'),
      axios.get('/api/v1/governance/metrics'),
    ]).then(([r, m]) => { setRegistry(r.data); setMetrics(m.data) })
      .catch(() => setErr(true))
  }, [])

  if (!session?.namespaceId) {
    return (
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">AI Governance</h2>
        <p className="text-gray-500">Not connected — visit <Link to="/setup" className="text-primary-600 underline">Workspace</Link> to connect a tenant.</p>
      </div>
    )
  }

  const M = metrics?.metrics
  const order = ['health', 'accuracy', 'drift', 'bias', 'explainability']

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-2 text-primary-600">
          <ShieldCheck size={18} />
          <span className="text-xs font-bold uppercase tracking-[0.14em]">Governance</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mt-1.5">Every model, governed end to end</h2>
        <p className="text-gray-500 mt-1">
          Live validation, monitoring &amp; documentation for <span className="font-medium">{session.name}</span> · {session.namespaceId}
          {metrics && <> · {metrics.conversations.total} conversations tracked</>}
        </p>
      </div>

      <GovernanceTrust />

      <GovernancePosture />

      <GovernanceQuality />

      {err && (
        <div className="card flex items-center gap-2 text-amber-700 bg-amber-500/5 border-amber-500/20">
          <AlertTriangle size={18} /> Couldn’t load governance data. Activate a service to start the record.
        </div>
      )}

      {/* Validation & Monitoring */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Validation &amp; Monitoring</h3>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {M ? order.map((k) => <MetricCard key={k} k={k} m={M[k]} />)
             : [...Array(5)].map((_, i) => <div key={i} className="card h-[132px] animate-pulse bg-gray-100" />)}
        </div>
      </section>

      {/* Model Documentation — the registry */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Model Documentation — every product</h3>
        <p className="text-sm text-gray-500 mb-3">
          A versioned fact sheet for every product the platform offers — what generates, what it embeds, and which agent governs it.
          {registry && <> {registry.active} live · {registry.available} available.</>}
        </p>

        <div className="space-y-3">
          {(registry?.registry || []).map((r) => {
            const meta = SERVICE_META[r.service] || { icon: Bot }
            const Icon = meta.icon
            const available = r.status === 'available'
            return (
              <div key={r.service} className={`card ${available ? 'opacity-75' : ''}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-600/10 text-primary-600 flex items-center justify-center"><Icon size={18} /></div>
                    <div>
                      <div className="font-semibold text-gray-900">{r.title || r.service}</div>
                      <div className="text-xs text-gray-400">Governed by {r.agent} · <span className="font-mono">{r.version}</span>{r.sourceUrl ? ` · ${r.sourceUrl.replace(/^https?:\/\//, '')}` : ''}</div>
                    </div>
                  </div>
                  {available
                    ? <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-gray-500/10 text-gray-500"><span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Available</span>
                    : <StatusPill status={r.status === 'active' ? 'good' : r.status === 'failed' ? 'critical' : 'baseline'} />}
                </div>

                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <Fact label="Generation model" value={r.model.generation} mono />
                  <Fact label="Fallback" value={r.model.fallback} mono />
                  <Fact label="Temperature" value={String(r.model.temperature)} />
                  <Fact label="Embedding" value={r.model.embedding || '—'} mono />
                  {r.knowledge && <>
                    <Fact label="Documents" value={r.knowledge.documents.toLocaleString()} />
                    <Fact label="Chunks (sources)" value={r.knowledge.chunks.toLocaleString()} />
                    <Fact label="Collections" value={String(r.knowledge.collections)} />
                  </>}
                  <Fact label="Refresh" value={r.refresh} />
                  <Fact label="Grounded" value={r.grounded ? 'Cited sources' : 'Generative'} />
                </div>
                {r.error && <p className="mt-3 text-xs text-red-600">Last error: {r.error}</p>}
              </div>
            )
          })}
        </div>
      </section>

      <p className="text-xs text-gray-400">Nothing ships ungoverned — every activation opens a documentation record and is validated &amp; monitored from day one.</p>
    </div>
  )
}

function Fact({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-gray-900 ${mono ? 'font-mono text-[12.5px]' : ''} mt-0.5 break-words`}>{value}</div>
    </div>
  )
}
